import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import aboutImage from '../assets/images/jetnseal-brick-clean-uk.png';

const { FiUser, FiClock, FiAward, FiHeart, FiTool, FiShield } = FiIcons;

const AboutUs = () => {
  const milestones = [
    { year: '2024', title: 'Jet\'n\'Seal Founded', description: 'Launched our professional cleaning services in North Yorkshire' },
    { year: 'Q2 2024', title: 'Rapid Growth', description: 'Expanded service coverage across Malton, Norton, and Pickering areas' },
    { year: 'Q3 2024', title: 'Customer Milestone', description: 'Reached 100+ satisfied customers in our first year' },
    { year: 'Q4 2024', title: 'Service Excellence', description: 'Achieved 5-star customer satisfaction rating' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">About Us</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Discover the trusted local experts behind Jet'n'Seal - your professional partner for patio cleaning and pressure washing services.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <img
                src={aboutImage}
                alt="Professional cleaning team at work"
                className="rounded-2xl shadow-lg w-full"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <h2 className="text-3xl font-bold text-gray-900">
                Passionate About Professional Cleaning
              </h2>
              
              <p className="text-lg text-gray-700 leading-relaxed">
                Like a wall built brick by brick, our work stands on a solid foundation of care, consistency, and craftsmanship. 
                Founded in 2024, Jet'n'Seal has quickly established itself as a trusted name in professional 
                outdoor cleaning services. Our company was built on a simple vision: to deliver exceptional 
                cleaning results while providing outstanding customer service to homeowners and businesses across 
                North Yorkshire.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed">
                What started as a mission to transform outdoor spaces has grown into a thriving local business 
                serving Malton, Norton, Pickering and surrounding areas. We take pride in every job, treating each 
                property with the same care and attention to detail that has earned us our reputation for excellence.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed">
                Our commitment is straightforward: to provide the highest quality patio cleaning and pressure washing 
                services whilst maintaining competitive prices and building lasting relationships with our customers. 
                We invest in professional-grade equipment and ongoing training to ensure we deliver superior results 
                every time.
              </p>
            </motion.div>
          </div>

          {/* Values Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-20"
          >
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Our Core Values</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: FiHeart,
                  title: 'Customer First',
                  description: 'Every decision is made with customer satisfaction as our top priority'
                },
                {
                  icon: FiAward,
                  title: 'Quality Excellence',
                  description: 'Using professional equipment and proven techniques for superior results'
                },
                {
                  icon: FiShield,
                  title: 'Trust & Reliability',
                  description: 'Fully insured with a reputation built on honest, dependable service'
                },
                {
                  icon: FiTool,
                  title: 'Professional Standards',
                  description: 'Continuous investment in the best cleaning technology and methods'
                },
                {
                  icon: FiUser,
                  title: 'Personal Touch',
                  description: 'Direct communication and personalised service for every customer'
                },
                {
                  icon: FiClock,
                  title: 'Punctuality',
                  description: 'Respecting your time with prompt arrival and efficient service delivery'
                }
              ].map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white p-8 rounded-xl shadow-lg text-center"
                >
                  <SafeIcon icon={value.icon} className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Journey Timeline */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Our Journey</h2>
            
            <div className="space-y-8">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className="flex flex-col md:flex-row items-center gap-8"
                >
                  <div className="flex-shrink-0">
                    <div className="w-24 h-24 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm px-2 text-center">
                      {milestone.year}
                    </div>
                  </div>
                  <div className="flex-1 bg-white p-6 rounded-xl shadow-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{milestone.title}</h3>
                    <p className="text-gray-600">{milestone.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
